import { useState, useRef, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  to: string;
  label: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: 'Content',
    items: [
      { to: '/content', label: 'Content' },
      { to: '/pathways', label: 'Pathways' },
      { to: '/tracks', label: 'Tracks' },
      { to: '/lessons', label: 'Lessons' },
      { to: '/achievements', label: 'Achievements' },
    ],
  },
  {
    label: 'Syncs',
    items: [
      { to: '/syncs', label: 'Syncs' },
      { to: '/syncs/jobs', label: 'Sync Jobs' },
      { to: '/syncs/sources', label: 'Article Sources' },
    ],
  },
];

function isGroupActive(group: NavGroup, pathname: string): boolean {
  return group.items.some(
    ({ to }) => pathname === to || (to !== '/' && pathname.startsWith(to)),
  );
}

function DropdownMenu({ group }: { group: NavGroup }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = isGroupActive(group, location.pathname);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1 text-sm font-medium px-2 py-1 rounded transition-colors ${
          active
            ? 'text-indigo-600'
            : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        {group.label}
        <svg
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-44 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1">
          {group.items.map(({ to, label }) => {
            const itemActive =
              location.pathname === to ||
              (to !== '/' && location.pathname.startsWith(to));
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className={`block px-4 py-2 text-sm font-medium transition-colors ${
                  itemActive
                    ? 'text-indigo-600 bg-indigo-50'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                {label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function Layout() {
  const { adminUser, signOut } = useAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6 min-w-0">
            <Link to="/" className="font-semibold text-slate-800 shrink-0">
              Neural Lumi Admin
            </Link>
            <nav className="flex items-center gap-1">
              {/* Dashboard standalone link */}
              <Link
                to="/"
                className={`text-sm font-medium px-2 py-1 rounded transition-colors ${
                  location.pathname === '/'
                    ? 'text-indigo-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Dashboard
              </Link>

              {navGroups.map((group) => (
                <DropdownMenu key={group.label} group={group} />
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm text-slate-500 hidden sm:block truncate max-w-[180px]">
              {adminUser?.email ?? adminUser?.uid}
            </span>
            <button
              onClick={() => signOut()}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 whitespace-nowrap"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
